import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, GripVertical, Trash2 } from 'lucide-react';
import { AdminEducationCategory, useDeleteCategory } from '@/hooks/useEducationAdmin';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';

interface CourseCardProps {
  category: AdminEducationCategory;
  onEdit: (category: AdminEducationCategory) => void;
}

export const CourseCard: React.FC<CourseCardProps> = ({ category, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const deleteCategory = useDeleteCategory();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDelete = async () => {
    await deleteCategory.mutateAsync(category.id);
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`transition-all duration-200 ${isDragging ? 'shadow-lg scale-105' : ''}`}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-2">
          <div
            className="cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">{category.name}</CardTitle>
            <Badge variant="outline" className="mt-1">
              Order: {category.sort_order}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {category.description && (
          <CardDescription>{category.description}</CardDescription>
        )}
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(category)}
          >
            <Edit className="mr-2 h-3 w-3" />
            Edit
          </Button>
          
          <ConfirmDialog
            title="Delete Course"
            description="Are you sure you want to delete this course? This action cannot be undone and will also delete all associated modules."
            confirmText="Delete"
            variant="destructive"
            onConfirm={handleDelete}
            isLoading={deleteCategory.isPending}
            trigger={
              <Button variant="outline" size="sm">
                <Trash2 className="mr-2 h-3 w-3" />
                Delete
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};